import { useState, useEffect, useMemo, useRef } from "react";

const LOGO_SCALE = { w: 40, h: 40, smW: 56, smH: 56 };

const SplashScreen = ({ onFinish }) => {
  const [phase, setPhase] = useState("enter"); // enter → loading → exit
  const startRef = useRef(Date.now());

  // Fixed duration
  const SPLASH_DURATION = 2200; // ms

  // Particle config (reused from Hero)
  const particles = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: i,
        left: `${Math.random() * 100}%`,
        delay: `${i * 0.5}s`,
        duration: `${15 + Math.random() * 10}s`,
        size: i % 2 === 0 ? "w-1.5 h-1.5" : "w-2 h-2",
        opacity: i % 2 === 0 ? "bg-[#D4AF7A]/30" : "bg-[#C9A882]/20",
      })),
    [],
  );

  // Sparkle dots around logo
  const sparkleDots = useMemo(
    () =>
      Array.from({ length: 6 }, (_, i) => ({
        id: i,
        top: `${10 + Math.random() * 80}%`,
        left: `${10 + Math.random() * 80}%`,
        size: 1.5 + Math.random() * 1.5,
        delay: `${i * 0.3}s`,
        duration: `${2 + Math.random() * 2}s`,
      })),
    [],
  );

  // Animated loading progress
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState("");

  const loadingMessages = useMemo(
    () => [
      { at: 0, text: "Menyiapkan henna..." },
      { at: 30, text: "Meracik pasta henna..." },
      { at: 55, text: "Mendesain pola..." },
      { at: 75, text: "Sentuhan akhir..." },
      { at: 90, text: "Siap! ✦" },
    ],
    [],
  );

  useEffect(() => {
    const start = startRef.current;
    const animate = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / SPLASH_DURATION) * 100, 100);
      setProgress(pct);

      // Update loading text
      const msg = [...loadingMessages].reverse().find((m) => pct >= m.at);
      if (msg) setLoadingText(msg.text);

      if (elapsed < SPLASH_DURATION) {
        requestAnimationFrame(animate);
      } else {
        setPhase("exit");
        // Delay for exit animation
        setTimeout(() => {
          onFinish();
        }, 600);
      }
    };
    requestAnimationFrame(animate);
  }, [loadingMessages, onFinish]);

  return (
    <div
      className={`fixed inset-0 z-[99999] flex flex-col items-center justify-center overflow-hidden transition-all duration-700 ${
        phase === "exit" ? "opacity-0 scale-[1.02]" : "opacity-100 scale-100"
      }`}
    >
      {/* Background Image with Overlay — sama dengan Hero */}
      <div className="absolute inset-0">
        <img
          src="./images/hero.png"
          alt=""
          className="object-cover w-full h-full lg:hidden"
          style={{ filter: "brightness(0.6)" }}
        />
        <img
          src="./images/hero.webp"
          alt=""
          className="hidden object-cover w-full h-full lg:block"
          style={{ filter: "brightness(0.6)" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-black/95"></div>
      </div>

      {/* Gold Radial Gradient — warm glow di tengah */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] max-w-full max-h-full pointer-events-none">
        <div className="w-full h-full bg-gradient-radial from-[#D4AF7A]/10 via-[#C9A882]/5 to-transparent rounded-full"></div>
      </div>

      {/* Decorative Background Blobs — enhanced */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#D4AF7A]/10 rounded-full blur-3xl animate-morph pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#C9A882]/8 rounded-full blur-3xl animate-float pointer-events-none"></div>
      <div
        className="absolute top-1/3 right-1/3 w-64 h-64 bg-[#E5C99A]/5 rounded-full blur-3xl animate-morph pointer-events-none"
        style={{ animationDuration: "10s, 7s", animationDelay: "2s, 0s" }}
      ></div>

      {/* Particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {particles.map((p) => (
          <div
            key={p.id}
            className={`absolute ${p.size} ${p.opacity} rounded-full animate-particle`}
            style={{
              left: p.left,
              animationDelay: p.delay,
              animationDuration: p.duration,
            }}
          />
        ))}
      </div>

      {/* Subtle noise grain overlay */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
          backgroundRepeat: "repeat",
          backgroundSize: "256px 256px",
        }}
      ></div>

      {/* Henna Pattern SVG Decoration */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-[0.06]">
        <svg
          viewBox="0 0 400 400"
          className="w-full h-auto max-w-lg"
          preserveAspectRatio="xMidYMid meet"
        >
          <g fill="none" stroke="#D4AF7A" strokeWidth="0.5">
            {/* Mandala inner */}
            <circle cx="200" cy="200" r="80" opacity="0.6" />
            <circle cx="200" cy="200" r="100" opacity="0.4" />
            <circle cx="200" cy="200" r="120" opacity="0.3" />
            <circle cx="200" cy="200" r="140" opacity="0.2" />
            {/* Petals */}
            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
              <ellipse
                key={angle}
                cx={200 + Math.cos((angle * Math.PI) / 180) * 90}
                cy={200 + Math.sin((angle * Math.PI) / 180) * 90}
                rx="30"
                ry="15"
                transform={`rotate(${angle}, 200, 200)`}
                opacity="0.25"
              />
            ))}
          </g>
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center">
        {/* Logo with animations — presisi sama dengan Hero */}
        <div className="relative mb-4 group sm:mb-8">
          {/* Morphing blob behind */}
          <div className="absolute inset-0 w-56 h-56 sm:w-80 sm:h-80 -top-8 -left-8 sm:-top-10 sm:-left-10 mx-auto bg-gradient-to-br from-[#D4AF7A]/15 via-[#C9A882]/10 to-transparent rounded-full animate-morph blur-sm"></div>

          {/* Gold glow */}
          <div className="absolute inset-0 bg-[#D4AF7A]/20 rounded-full blur-xl animate-glow"></div>

          {/* Decorative gold rings */}
          <div className="absolute inset-0 w-44 h-44 sm:w-64 sm:h-64 -top-2 -left-2 sm:-top-2 sm:-left-2 rounded-full border border-[#D4AF7A]/30 animate-spin-slow shadow-[0_0_30px_rgba(212,175,122,0.15)]"></div>
          <div
            className="absolute inset-0 w-40 h-40 sm:w-60 sm:h-60 -top-0 -left-0 sm:top-0 sm:left-0 rounded-full border border-[#D4AF7A]/10 animate-spin-slow"
            style={{ animationDirection: "reverse", animationDuration: "25s" }}
          ></div>

          {/* Sparkle dots */}
          {sparkleDots.map((dot) => (
            <div
              key={dot.id}
              className="absolute rounded-full bg-[#D4AF7A] animate-pulse pointer-events-none"
              style={{
                top: dot.top,
                left: dot.left,
                width: `${dot.size}px`,
                height: `${dot.size}px`,
                animationDelay: dot.delay,
                animationDuration: dot.duration,
              }}
            />
          ))}

          {/* Logo image — ukuran presisi sama dengan Hero */}
          <img
            src="./logo512.png"
            alt="Putri Henna Artist"
            className="relative w-auto h-40 transition-transform duration-500 sm:h-60 animate-logo-float"
          />
        </div>

        {/* Decorative gold divider */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-[#D4AF7A]/50 to-[#D4AF7A]/20"></div>
          <span className="text-[#D4AF7A] text-lg sm:text-xl font-serif">
            &diams;
          </span>
          <div className="w-16 sm:w-24 h-px bg-gradient-to-l from-transparent via-[#D4AF7A]/50 to-[#D4AF7A]/20"></div>
        </div>

        {/* Brand text — presisi sama dengan Hero */}
        <div className="hidden sm:block">
          <h1 className="text-xl font-bold text-[#D4AF7A] tracking-wide">
            Teh Putri
          </h1>
          <p className="text-xs text-[#F7F5F2] tracking-wider">HENNA ARTIST</p>
        </div>
        <div className="sm:hidden">
          <h1 className="text-lg font-bold text-[#D4AF7A] tracking-wide">
            Teh Putri
          </h1>
          <p className="text-xs text-[#F7F5F2] tracking-wider">HENNA ARTIST</p>
        </div>

        {/* Loading bar */}
        <div className="relative w-48 mt-8 sm:w-64">
          {/* Track */}
          <div className="w-full h-[3px] bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#C9A882] via-[#D4AF7A] to-[#E5C99A] rounded-full transition-none"
              style={{ width: `${progress}%` }}
            />
          </div>

          {/* Loading text */}
          <p className="text-center text-[#F7F5F2]/50 text-xs mt-3 transition-opacity duration-300">
            {loadingText}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SplashScreen;
